import type {
  CreatePasswordlessUserInput,
  ScopelessUser,
} from '@sunrise-ui/api/cerebro/api';
import type { JoinCommunityInput } from '@sunrise-ui/api/network';
import type {
  CreateProfileInput,
  UpdateProfileInput,
  UpdateProfileLinksInput,
} from '@sunrise-ui/api/profile/api';
import {
  useAuthStore,
  useCacheAvailability,
  useSnackbar,
} from '@sunrise-ui/primitives';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { QueryKeys } from '../constants/QueryKeys';
import {
  adminProfilesApi,
  connectionsApi,
  membershipsApi,
  profilesApi,
  useAuthInfiniteQuery,
  useAuthMutation,
  useAuthQuery,
} from '../utils/api';
import { assertParameterIsNotUndefinedOrNull } from '../utils/error';
import { bulkGetProfiles } from '../utils/profiles';

const { bulkProfilesKey, profileKey, searchKey, blockedUsersKey } =
  QueryKeys.profiles;
const { myConnectionsKey } = QueryKeys.connections;

const { tagsKey, canonicalKey, byCommunityKey } = QueryKeys.profiles;

export const useBulkGetProfiles = (userIds?: string[]) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryFn: async () => bulkGetProfiles(queryClient, userIds),
    queryKey: [bulkProfilesKey, userIds],
    enabled: Boolean(userIds?.length),
  });
};

export const useGetCanonicalProfileTagsByCommunity = (communityId?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');

      const response =
        await profilesApi.getCanonicalTagsProfilesCommunitiesCommunityIdTagsGet(
          {
            communityId,
          },
        );

      return response.data;
    },
    queryKey: [profileKey, tagsKey, canonicalKey, byCommunityKey, communityId],
    enabled: Boolean(communityId),
  });
};

/**
 * Update a profile (Profiles service)
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async (updateProfileInput: UpdateProfileInput) => {
      const response = await profilesApi.updateProfileProfilesPut({
        updateProfileInput,
      });
      return response.data;
    },
    onSuccess: (_data, { user_id }) => {
      void queryClient.invalidateQueries({ queryKey: [profileKey, user_id] });
      void queryClient.invalidateQueries({ queryKey: [profileKey, searchKey] });
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      showSnackbar({ text: 'Failed to update profile', type: 'error' });
    },
  });
};

/**
 * Create a user with password in Cerebro
 */
export const useCreateUser = () => {
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async ({
      email,
      password,
      phone_number,
    }: {
      email: string;
      password: string;
      phone_number?: string;
    }) => {
      const response = await adminProfilesApi.createUserProfilesPost({
        userCreateInput: { email, password, phone_number },
      });
      return response.data as ScopelessUser;
    },
    onError: (error) => {
      console.error('Error creating user:', error);
      if (isAxiosError(error) && error.response?.status === 409) {
        showSnackbar({
          text: 'User with this email already exists',
          type: 'error',
        });
        return;
      }
      showSnackbar({ text: 'Failed to create user', type: 'error' });
    },
  });
};

/**
 * Create a passwordless user (Cerebro) given an email address.
 */
export const useCreatePasswordlessUser = () => {
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async ({
      email,
      phone_number,
    }: CreatePasswordlessUserInput) => {
      const response =
        await adminProfilesApi.createPasswordlessUserProfilesCreatePasswordlessUserPost(
          {
            createPasswordlessUserInput: { email, phone_number },
          },
        );
      return response.data as ScopelessUser;
    },
    onError: (error) => {
      console.error('Error creating user:', error);
      showSnackbar({ text: 'Failed to create user', type: 'error' });
    },
  });
};

/**
 * Create a profile for a given user id (Profiles service)
 */
export const useCreateProfile = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async (createProfileInput: CreateProfileInput) => {
      const response = await profilesApi.createProfileProfilesPost({
        createProfileInput,
      });
      return response.data;
    },
    onSuccess: (_data, { user_id }) => {
      void queryClient.invalidateQueries({ queryKey: [profileKey, user_id] });
      void queryClient.invalidateQueries({ queryKey: [profileKey, searchKey] });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        showSnackbar({ text: 'User already exists', type: 'error' });
        return;
      }
      console.error('Error creating profile:', error);
      showSnackbar({ text: 'Failed to create profile', type: 'error' });
    },
  });
};

/**
 * Update profile links (e.g., LinkedIn)
 */
export const useUpdateProfileLinks = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async ({
      userId,
      links,
    }: {
      userId: string;
      links: UpdateProfileLinksInput;
    }) => {
      const response =
        await profilesApi.updateProfileLinksProfilesUsersUserIdLinksPut({
          userId,
          updateProfileLinksInput: links,
        });
      return response.data;
    },
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: [profileKey, userId] });
      void queryClient.invalidateQueries({ queryKey: [profileKey, searchKey] });
    },
    onError: (error) => {
      console.error('Error updating profile links:', error);
      showSnackbar({ text: 'Failed to update profile links', type: 'error' });
    },
  });
};

/**
 * Update profile photo (headshot)
 */
export const useUpdateProfilePhoto = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async ({ userId, file }: { userId: string; file: File }) => {
      const response =
        await profilesApi.updatePhotoProfilesUsersUserIdPhotosPut({
          userId,
          file,
        });
      return response.data;
    },
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: [profileKey, userId] });
    },
    onError: (error) => {
      console.error('Error uploading profile photo:', error);
      showSnackbar({ text: 'Failed to upload photo', type: 'error' });
    },
  });
};

/**
 * Optionally add a user to a community (Cerebro) — useful for manager flows
 */
export const useCreateCommunityUser = () => {
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async ({
      communityId,
      userId,
      isOrganizer,
    }: {
      communityId: string;
      userId: string;
      isOrganizer?: boolean;
    }) => {
      // Use profile service (Network) to join community
      const joinCommunityInput: JoinCommunityInput = {
        community_id: communityId,
        user_id: userId,
      };

      try {
        await membershipsApi.joinCommunityMembershipsJoinPost({
          joinCommunityInput,
        });
      } catch (error) {
        // If user is already a member (409 conflict), that's okay - continue
        if (isAxiosError(error) && error.response?.status !== 409) {
          throw error;
        }
        console.log('User already a member of community, continuing...');
      }

      // If organizer, set organizer status
      if (isOrganizer) {
        await membershipsApi.updateMemberOrganizerStatusMembershipsCommunitiesCommunityIdMembersUserIdOrganizerStatusPatch(
          {
            communityId,
            userId,
            updateMemberOrganizerStatusInput: { is_organizer: true },
          },
        );
      }

      return { userId };
    },
    onError: (error) => {
      console.error('Error adding user to community:', error);
      showSnackbar({ text: 'Failed to add user to community', type: 'error' });
    },
  });
};

export const useSearchProfiles = (searchQuery?: string) =>
  useAuthInfiniteQuery({
    queryFn: async ({ pageParam }) => {
      assertParameterIsNotUndefinedOrNull(searchQuery, 'searchQuery');
      const response = await profilesApi.searchProfilesProfilesSearchPost({
        searchProfilesInput: {
          query: searchQuery,
          offset: pageParam,
          limit: 20,
        },
      });
      return { ...response.data, searchQuery };
    },
    initialPageParam: 0,
    getNextPageParam(lastPage): number | undefined {
      if (lastPage?.next) return lastPage.offset + lastPage.limit;
    },
    getPreviousPageParam(lastPage): number | undefined {
      if (lastPage?.previous) return lastPage.offset - lastPage.limit;
    },
    queryKey: [profileKey, searchKey, searchQuery],
    enabled: Boolean(searchQuery && searchQuery.length > 2),
    placeholderData: keepPreviousData,
  });

/**
 * Fetches the current user's connections with their profiles
 */
export const useGetMyConnectionsWithProfiles = () => {
  const queryClient = useQueryClient();
  const { currentUserId } = useAuthStore();

  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const connectionsResponse =
        await connectionsApi.getConnectionsConnectionsUserIdGet({
          userId: currentUserId,
        });

      const connectionUserIds = connectionsResponse.data.map(
        (person) => person.user_id,
      );

      if (connectionUserIds.length === 0) {
        return [];
      }

      const profiles = await bulkGetProfiles(queryClient, connectionUserIds);
      return profiles;
    },
    queryKey: [myConnectionsKey, currentUserId, 'withProfiles'],
    enabled: Boolean(currentUserId),
  });
};

export const useBlockUser = (userId?: string) => {
  const queryClient = useQueryClient();

  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    mutationFn: async () => {
      if (!userId) return;

      // Get currentUserId at execution time to avoid stale closure issues
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response = await profilesApi.blockUserProfilesUsersUserIdBlocksPost(
        {
          userId: currentUserId,
          blockedUserInput: {
            blocked_user_id: userId,
          },
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      // Get currentUserId at execution time for cache invalidation
      const { currentUserId } = useAuthStore.getState();
      await queryClient.invalidateQueries({
        queryKey: [profileKey, blockedUsersKey, currentUserId],
      });
      showSnackbar({
        text: 'Blocked user',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to block user. Please try again',
        type: 'error',
      });
    },
  });
};

export const useGetBlockedUsersProfiles = () => {
  const { currentUserId } = useAuthStore();
  return useAuthInfiniteQuery({
    queryFn: async ({ pageParam }) => {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const response =
        await profilesApi.getBlockedUsersProfilesUsersUserIdBlocksGet({
          userId: currentUserId,
          offset: pageParam,
          limit: 100,
        });
      return response.data;
    },
    queryKey: [profileKey, blockedUsersKey, currentUserId],
    initialPageParam: 0,
    getNextPageParam(lastPage): number | undefined {
      if (lastPage?.next) return lastPage.offset + lastPage.limit;
    },
    getPreviousPageParam(lastPage): number | undefined {
      if (lastPage?.previous) return lastPage.offset - lastPage.limit;
    },
    staleTime: 600000,
  });
};

/**
 * Hook to fetch a single user profile by ID
 */
export const useGetProfile = (userId?: string, enabled: boolean = true) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(userId, 'userId');
      return getProfile(userId);
    },
    queryKey: [profileKey, userId],
    enabled: enabled && Boolean(userId) && !isQueryKeyAvailable([profileKey]),
    staleTime: 300000, // 5 minutes
  });
};

export const getProfile = async (userId: string) => {
  const response = await profilesApi.getProfileProfilesUsersUserIdGet({
    userId: userId,
  });
  return response.data;
};
